/**
 * Theme Context
 *
 * Provides theme state and toggle functionality throughout the application.
 * Persists theme preference to localStorage.
 */

import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'theme';

/**
 * Theme context value interface
 */
export interface ThemeContextValue {
  /** Current theme */
  theme: Theme;
  /** Toggle between light and dark themes */
  toggleTheme: () => void;
  /** Set a specific theme */
  setTheme: (theme: Theme) => void;
}

/**
 * Default context value (used when context is accessed outside provider)
 */
const defaultContextValue: ThemeContextValue = {
  theme: 'light',
  toggleTheme: () => {
    throw new Error('ThemeProvider not found');
  },
  setTheme: () => {
    throw new Error('ThemeProvider not found');
  },
};

/**
 * Theme context
 */
export const ThemeContext = createContext<ThemeContextValue>(defaultContextValue);

/**
 * Props for ThemeProvider component
 */
export interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Get initial theme from localStorage or default to light
 */
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') {
    return stored;
  }

  return 'light';
}

/**
 * Theme Provider component
 *
 * Wraps the application and provides theme state and toggle functionality
 * to all child components via React Context.
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Apply theme to document on mount and when theme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  /**
   * Toggle between light and dark themes
   */
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  /**
   * Set a specific theme
   */
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  /**
   * Memoized context value
   */
  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      theme,
      toggleTheme,
      setTheme,
    }),
    [theme, toggleTheme, setTheme]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};
